import { useState } from 'react';
import {
  Layout,
  Menu,
  Typography,
  Space,
  Select,
  Card,
  Divider,
  Button,
  message,
  Modal,
  Row,
  Col,
  Tag,
  Switch,
  theme,
} from 'antd';
import {
  UserOutlined,
  AppstoreOutlined,
  CodeOutlined,
  SettingOutlined,
  CloudOutlined,
  LockOutlined,
  GlobalOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';
import { useFontSize } from '../contexts/FontSizeContext';
import { useCompactAlgorithm } from '../contexts/CompactAlgorithmContext';
import { useDarkMode } from '../contexts/DarkModeContext';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const SettingsPanel = () => {
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation();
  const [selectedKey, setSelectedKey] = useState('general');
  const { currentTheme, availableThemes, setTheme } = useTheme();
  const { fontSize, setFontSize } = useFontSize();
  const { isCompactEnabled, setIsCompactEnabled } = useCompactAlgorithm();
  const { isDarkMode, setIsDarkMode, followSystem, setFollowSystem } = useDarkMode();

  const menuItems = [
    {
      key: 'general',
      icon: <SettingOutlined />,
      label: t('settings.general.title'),
      disabled: false,
    },
    {
      key: 'appearance',
      icon: <BgColorsOutlined />,
      label: t('settings.appearance.title'),
      disabled: false,
    },
    {
      key: 'editor',
      icon: <CodeOutlined />,
      label: t('settings.editor.title'),
      disabled: true,
    },
    {
      key: 'account',
      icon: <UserOutlined />,
      label: t('settings.account.title'),
      disabled: true,
    },
    {
      key: 'cloud',
      icon: <CloudOutlined />,
      label: t('settings.cloud.title'),
      disabled: true,
    },
    {
      key: 'security',
      icon: <LockOutlined />,
      label: t('settings.security.title'),
      disabled: true,
    },
  ];

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
  };

  const handleClearCache = () => {
    Modal.confirm({
      title: t('settings.general.clearCacheConfirmTitle'),
      content: t('settings.general.clearCacheConfirmContent'),
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
      okType: 'danger',
      onOk: () => {
        localStorage.clear();
        message.success(t('settings.general.clearCacheSuccess'));
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      },
    });
  };

  // Supported languages
  const supportedLanguages = ['en', 'nl', 'fr', 'de'];
  // Determine dropdown value: use i18n.language if supported, else fallback to 'en'
  const dropdownLanguage = supportedLanguages.includes(i18n.language) ? i18n.language : 'en';

  const renderGeneralSettings = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <GlobalOutlined />
            <span>{t('settings.general.language.label')}</span>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">{t('settings.general.language.description')}</Text>
          <Select
            value={dropdownLanguage}
            onChange={handleLanguageChange}
            style={{ width: 200 }}
            size="large"
            data-testid="language-selector"
          >
            <Option value="en">
              <Space>
                <span>🇺🇸</span>
                <span>{t('languages.en')}</span>
              </Space>
            </Option>
            <Option value="nl">
              <Space>
                <span>🇳🇱</span>
                <span>{t('languages.nl')}</span>
              </Space>
            </Option>
            <Option value="fr">
              <Space>
                <span>🇫🇷</span>
                <span>{t('languages.fr')}</span>
              </Space>
            </Option>
            <Option value="de">
              <Space>
                <span>🇩🇪</span>
                <span>{t('languages.de')}</span>
              </Space>
            </Option>
          </Select>
          <Divider />
          <Button danger onClick={handleClearCache} style={{ width: 200 }}>
            {t('settings.general.clearCache')}
          </Button>
        </Space>
      </Card>
    </Space>
  );

  const renderAppearanceSettings = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title={t('settings.appearance.darkMode.label')}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>{t('settings.appearance.darkMode.label')}</Text>
            <br />
            <Text type="secondary">{t('settings.appearance.darkMode.description')}</Text>
          </div>
          
          {/* Follow System Option */}
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space>
              <Switch
                checked={followSystem}
                onChange={setFollowSystem}
                size="default"
              />
              <Text strong>Follow System</Text>
            </Space>
            <Text type="secondary" style={{ marginLeft: 44 }}>
              Automatically switch between light and dark mode based on your system settings
            </Text>
          </Space>

          <Divider style={{ margin: '8px 0' }} />
          
          {/* Manual Dark Mode Toggle - disabled when following system */}
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space>
              <Switch
                checked={isDarkMode}
                onChange={setIsDarkMode}
                disabled={followSystem}
                size="default"
              />
              <Text strong style={{ opacity: followSystem ? 0.5 : 1 }}>
                {isDarkMode ? 'Dark Mode' : 'Light Mode'}
              </Text>
            </Space>
            <Text type="secondary" style={{ marginLeft: 44, opacity: followSystem ? 0.5 : 1 }}>
              Manually control dark mode (disabled when following system)
            </Text>
          </Space>
        </Space>
      </Card>

      <Card title="Theme Selection">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>{t('settings.appearance.theme.label')}</Text>
            <br />
            <Text type="secondary">{t('settings.appearance.theme.description')}</Text>
          </div>
          
          <Select
            value={currentTheme.id}
            onChange={setTheme}
            style={{ width: '100%' }}
            size="large"
          >
            {availableThemes.map(theme => (
              <Option key={theme.id} value={theme.id}>
                <Space>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      display: 'inline-block',
                    }}
                  />
                  <span>{theme.name}</span>
                </Space>
              </Option>
            ))}
          </Select>
          
          <Card size="small" style={{ marginTop: 16 }}>
            <Title level={5}>Current Theme: {currentTheme.name}</Title>
            <Text type="secondary">{currentTheme.description}</Text>
            
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Space direction="vertical" size="small">
                  <Text strong>Brand</Text>
                  <Text>{currentTheme.branding.appName}</Text>
                  <Text>{currentTheme.branding.companyName}</Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" size="small">
                  <Text strong>Colors</Text>
                  <Space>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: currentTheme.colors.primary,
                        borderRadius: 4,
                        border: '1px solid #d9d9d9',
                      }}
                      title="Primary"
                    />
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: currentTheme.colors.secondary,
                        borderRadius: 4,
                        border: '1px solid #d9d9d9',
                      }}
                      title="Secondary"
                    />
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        backgroundColor: currentTheme.colors.background,
                        borderRadius: 4,
                        border: '1px solid #d9d9d9',
                      }}
                      title="Background"
                    />
                  </Space>
                </Space>
              </Col>
            </Row>
          </Card>
        </Space>
      </Card>

      <Card title="Font Size">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>{t('settings.appearance.fontSize.label')}</Text>
            <br />
            <Text type="secondary">{t('settings.appearance.fontSize.description')}</Text>
          </div>
          
          <Select
            value={fontSize}
            onChange={setFontSize}
            style={{ width: '100%' }}
            size="large"
          >
            <Option value="large">
              <span style={{ fontSize: '18px' }}>{t('settings.appearance.fontSize.large')}</span>
            </Option>
            <Option value="medium">
              <span style={{ fontSize: '16px' }}>{t('settings.appearance.fontSize.medium')}</span>
            </Option>
            <Option value="small">
              <span style={{ fontSize: '14px' }}>{t('settings.appearance.fontSize.small')}</span>
            </Option>
            <Option value="very-small">
              <span style={{ fontSize: '12px' }}>{t('settings.appearance.fontSize.verySmall')}</span>
            </Option>
          </Select>
        </Space>
      </Card>

      <Card title={t('settings.appearance.compactAlgorithm.label')}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong>{t('settings.appearance.compactAlgorithm.label')}</Text>
            <br />
            <Text type="secondary">{t('settings.appearance.compactAlgorithm.description')}</Text>
          </div>
          
          <Space>
            <Switch
              checked={isCompactEnabled}
              onChange={setIsCompactEnabled}
              size="default"
            />
            <Text>{isCompactEnabled ? t('settings.appearance.compactAlgorithm.enabled') : t('settings.appearance.compactAlgorithm.disabled')}</Text>
          </Space>
        </Space>
      </Card>
    </Space>
  );

  const renderDefaultSettings = (category: string) => {
    const placeholderKey = `settings.${category}.placeholder`;
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ padding: '24px', borderRadius: '8px' }}>
          <p>{t(placeholderKey)}</p>
        </div>
      </Space>
    );
  };

  const renderContent = () => {
    switch (selectedKey) {
      case 'general':
        return renderGeneralSettings();
      case 'appearance':
        return renderAppearanceSettings();
      default:
        return renderDefaultSettings(selectedKey);
    }
  };

  const getContentTitle = () => {
    const item = menuItems.find((item) => item.key === selectedKey);
    return item ? item.label : t('settings.title');
  };

  return (
    <Layout style={{ height: '100%', overflow: 'hidden' }}>
      <Sider
        width={250}
        style={{ borderRight: '1px solid', borderColor: token.colorBorder, paddingRight: 0, backgroundColor: token.colorBgContainer }}
      >
        <div style={{ padding: '24px 0 16px 24px', textAlign: 'left' }}>
          <Title level={4} style={{ margin: 0, textAlign: 'left' }}>
            {t('settings.title')}
          </Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onSelect={({ key }) => setSelectedKey(key)}
        />
      </Sider>
      <Content style={{ padding: '24px', overflow: 'auto', height: '100%' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Title level={3}>{getContentTitle()}</Title>
          {renderContent()}
        </Space>
      </Content>
    </Layout>
  );
};

export default SettingsPanel;
